// Tank skin registry — the cosmetic identities a player can equip on their
// tank. Skins are account-level entitlements (DB `user_skins`, keyed to the
// webolo account) so they follow the player across devices and platforms:
// a skin earned or purchased on iOS shows up on macOS, tvOS, Steam, and the
// web client automatically.
//
// CROSS-CLIENT CONTRACT: mirrored manually in
// `desktop/Assets/Scripts/Protocol/Skins.cs`. Ids are the wire contract —
// never rename; append-only.
//
// "default" is implicit: every player owns it, it never appears in
// `user_skins`, and the server omits PlayerState.skin entirely when it's
// equipped (so pre-skin clients see byte-identical snapshots).

export type SkinId = "default" | "silver" | "gold" | "skynet" | "mars";

// Rarity drives the lobby presentation (tile frame colour, sheen animation
// on legendaries). Pure client-side flavour; the server never reads it.
export type SkinRarity = "common" | "rare" | "epic" | "legendary";

export interface SkinDef {
  id: SkinId;
  name: string;
  description: string;
  rarity: SkinRarity;
}

export const SKINS: readonly SkinDef[] = [
  {
    id: "default",
    name: "STANDARD ISSUE",
    description: "Factory-fresh armour in your colour of choice.",
    rarity: "common",
  },
  {
    id: "silver",
    name: "SILVER SIEGE",
    description: "Polished silver plating for proven siege defenders.",
    rarity: "rare",
  },
  {
    id: "gold",
    name: "GOLD SIEGE",
    description: "Reflective gold armour. Only siege legends carry it.",
    rarity: "epic",
  },
  {
    id: "skynet",
    name: "HUNTER-KILLER",
    description: "Captured SkyNet chassis, re-flagged for the resistance.",
    rarity: "legendary",
  },
  {
    id: "mars",
    name: "MARS HOVER",
    description: "White off-world hover tank. Colour picks tint the glow.",
    rarity: "legendary",
  },
];

export const SKIN_IDS: readonly SkinId[] = SKINS.map((s) => s.id);

export function isSkinId(s: string): s is SkinId {
  return (SKIN_IDS as readonly string[]).includes(s);
}

// The default skin every account implicitly owns.
export const DEFAULT_SKIN_ID: SkinId = "default";
